import { useCallback } from "react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { StatCard } from "../components/common/StatCard";
import { DataTable } from "../components/common/DataTable";
import { FilterBar } from "../components/common/FilterBar";
import { PageHeader } from "../components/common/PageHeader";
import { useDataFetching } from "../hooks/useDataFetching";
import { useFiltering } from "../hooks/useFiltering";
import { walksAPI, schoolsAPI } from "../api";
import { QUERY_KEYS, WALK_STATUSES } from "../utils/constants";
import { formatDateTime } from "../utils/formatters";
import { getSchoolName, handleSchoolId } from "../utils/helpers";
import { SchoolFilter } from "../components/filters/SchoolFilter";
import { MapPin, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { Walk, ApiDataResponse, FilterFunction } from "../types";

export function WalksPage() {
  const {
    data: walksData,
    isLoading: walksLoading,
    error: walksError,
  } = useDataFetching(QUERY_KEYS.WALKS, {
    queryFn: walksAPI.getAllWalks,
  });

  const { data: schoolsData } = useDataFetching(QUERY_KEYS.SCHOOLS, {
    queryFn: schoolsAPI.getAllSchools,
  });

  const walks = Array.isArray(walksData) ? walksData : ((walksData as ApiDataResponse<Walk[]>)?.data || []);
  const schools = Array.isArray(schoolsData) ? schoolsData : [];

  const {
    searchTerm,
    setSearchTerm,
    filters,
    updateFilter,
    filteredData: filteredWalks,
  } = useFiltering({
    data: walks,
    searchFields: ['_id'],
    filterFn: ((walk: Walk, filters) => {
      const schoolMatch = filters.schoolId
        ? handleSchoolId(walk.school_id) === filters.schoolId
        : true;
      return schoolMatch;
    }) as FilterFunction<Walk>,
  });

  const pendingWalks = filteredWalks.filter(
    (walk: Walk) => walk.status === WALK_STATUSES.PENDING
  ).length;
  const activeWalks = filteredWalks.filter(
    (walk: Walk) => walk.status === WALK_STATUSES.ACTIVE
  ).length;
  const completedWalks = filteredWalks.filter(
    (walk: Walk) => walk.status === WALK_STATUSES.COMPLETED
  ).length;
  const cancelledWalks = filteredWalks.filter(
    (walk: Walk) => walk.status === WALK_STATUSES.CANCELLED || walk.status === WALK_STATUSES.CLOSED
  ).length;

  const handleSchoolChange = useCallback((schoolId: string) => {
    updateFilter('schoolId', schoolId);
  }, [updateFilter]);

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { className: string; icon: React.ReactNode }> = {
      [WALK_STATUSES.COMPLETED]: {
        className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        icon: <CheckCircle className="h-3 w-3" />,
      },
      [WALK_STATUSES.ACTIVE]: {
        className: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200",
        icon: <Clock className="h-3 w-3" />,
      },
      [WALK_STATUSES.PENDING]: {
        className: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
        icon: <AlertCircle className="h-3 w-3" />,
      },
      [WALK_STATUSES.CANCELLED]: {
        className: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
        icon: <XCircle className="h-3 w-3" />,
      },
      [WALK_STATUSES.CLOSED]: {
        className: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
        icon: <XCircle className="h-3 w-3" />,
      },
    };

    const config = statusConfig[status] || {
      className: "bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200",
      icon: null,
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.icon}
        {status}
      </span>
    );
  };

  const columns = [
    {
      key: '_id',
      header: 'ID',
      render: (walk: Walk) => (
        <span className="font-medium font-mono text-sm">
          {walk._id.substring(0, 8)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (walk: Walk) => getStatusBadge(walk.status),
    },
    {
      key: 'school_id',
      header: 'School',
      render: (walk: Walk) => getSchoolName(handleSchoolId(walk.school_id), schools),
    },
    {
      key: 'started_at',
      header: 'Started',
      render: (walk: Walk) => (
        <span className="text-muted-foreground">
          {formatDateTime(walk.started_at)}
        </span>
      ),
    },
    {
      key: 'ended_at',
      header: 'Ended',
      render: (walk: Walk) => (
        <span className="text-muted-foreground">
          {walk.ended_at ? formatDateTime(walk.ended_at) : '-'}
        </span>
      ),
    },
  ];

  if (walksLoading) {
    return (
      <DashboardLayout>
        <LoadingState type="table" />
      </DashboardLayout>
    );
  }

  if (walksError) {
    return (
      <DashboardLayout>
        <ErrorState
          title="Error loading walks data"
          message="Please try refreshing the page or log in again"
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="responsive-container">
        <div className="space-y-6">
          <PageHeader
            title="Walks (Legacy)"
            description="View historical walk data from the legacy tracking system"
          />

          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
            <StatCard
              title="Total Walks"
              value={filteredWalks.length}
              description="All walks"
              icon={<MapPin className="h-4 w-4" />}
              variant="blue"
              isLoading={walksLoading}
            />

            <StatCard
              title="Pending"
              value={pendingWalks}
              description="Awaiting start"
              icon={<AlertCircle className="h-4 w-4" />}
              variant="amber"
              isLoading={walksLoading}
            />

            <StatCard
              title="Active"
              value={activeWalks}
              description="Currently active"
              icon={<Clock className="h-4 w-4" />}
              variant="cyan"
              isLoading={walksLoading}
            />

            <StatCard
              title="Completed"
              value={completedWalks}
              description="Successfully finished"
              icon={<CheckCircle className="h-4 w-4" />}
              variant="green"
              isLoading={walksLoading}
            />

            <StatCard
              title="Cancelled/Closed"
              value={cancelledWalks}
              description="Not completed"
              icon={<XCircle className="h-4 w-4" />}
              variant="red"
              isLoading={walksLoading}
            />
          </div>

          <DataTable
            data={filteredWalks}
            columns={columns}
            title="All Walks"
            headerActions={
              <FilterBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search walks by ID..."
                resultCount={{
                  filtered: filteredWalks.length,
                  total: walks.length,
                }}
              >
                <SchoolFilter
                  selectedSchoolId={filters.schoolId ? String(filters.schoolId) : ''}
                  onSchoolChange={handleSchoolChange}
                />
              </FilterBar>
            }
            emptyMessage="No walks found"
            emptyDescription={
              searchTerm || filters.schoolId
                ? "Try adjusting your search filters"
                : "No walks have been started yet"
            }
            isLoading={walksLoading}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}                         